import React from 'react';
import { HeartPulse, Award, Users, Clock } from 'lucide-react';
import PageMeta from '../components/PageMeta';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Nursing = () => {
  const nursingTypes = [
    {
      title: 'Registered Nurses (RN)',
      description: 'Highly qualified registered nurses with diverse specializations and extensive clinical experience.',
    },
    {
      title: 'Licensed Practical Nurses (LPN)',
      description: 'Skilled LPNs ready to provide quality patient care under the supervision of registered nurses and physicians.',
    },
    {
      title: 'Certified Nursing Assistants (CNA)',
      description: 'Compassionate CNAs dedicated to assisting patients with daily activities and basic care needs.',
    },
    {
      title: 'Specialty Nursing',
      description: 'Nurses with specialized certifications in areas such as ICU, ER, Pediatrics, Oncology, and more.',
    },
  ];

  const benefits = [
    {
      icon: HeartPulse,
      title: 'Patient-Centered Care',
      description: 'Our nurses prioritize patient well-being and deliver compassionate, high-quality care.',
    },
    {
      icon: Award,
      title: 'Highly Qualified',
      description: 'All nurses undergo rigorous vetting and possess current certifications and licenses.',
    },
    {
      icon: Users,
      title: 'Team Integration',
      description: 'Our nurses seamlessly integrate into your team and adapt to your facility\'s protocols.',
    },
    {
      icon: Clock,
      title: 'Flexible Scheduling',
      description: 'We offer flexible staffing solutions including per diem, travel, and permanent placements.',
    },
  ];

  return (
    <div className="nursing-page">
      <PageMeta
        title="Nursing"
        description="Skilled nursing professionals: RNs, LPNs, CNAs, and specialty nurses. Patient-centered care from BluekraftMed's vetted network."
        path="/nursing"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Nursing Services</h1>
            <p className="text-xl leading-relaxed">
              Skilled nursing professionals ready to provide exceptional care
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Professional Nursing Staff</h2>
              <p className="text-gray-600 text-lg mb-4 leading-relaxed">
                At BluekraftMed, we understand the critical role nurses play in delivering quality healthcare. Our nursing professionals are the backbone of patient care, combining clinical expertise with compassionate service.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We provide a comprehensive range of nursing services, from registered nurses to certified nursing assistants, all carefully vetted and ready to meet your facility's unique needs.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=800&h=600&fit=crop"
                alt="Nursing Professional"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nursing Types */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Nursing Services</h2>
            <p className="text-gray-600 text-lg">Comprehensive nursing solutions for every healthcare setting</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {nursingTypes.map((type, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardHeader>
                  <CardTitle className="text-2xl">{type.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-lg leading-relaxed">{type.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Our Nurses</h2>
            <p className="text-gray-600 text-lg">The BluekraftMed nursing advantage</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle>{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Nursing Professionals</h2>
            <p className="text-gray-600 text-lg">Meet our dedicated healthcare heroes</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <img
              src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?w=600&h=400&fit=crop"
              alt="Nurse"
              className="w-full h-80 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
            />
            <img
              src="https://images.unsplash.com/photo-1621862912856-0909fb7f14b7?w=600&h=400&fit=crop"
              alt="Nurse"
              className="w-full h-80 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
            />
            <img
              src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=600&h=400&fit=crop"
              alt="Healthcare Professional"
              className="w-full h-80 object-cover rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Need Qualified Nursing Staff?</h2>
            <p className="text-xl mb-8">
              Contact us today to discuss your nursing staffing requirements
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-white text-cyan-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-cyan-600 px-8 py-6 text-lg">
                Call: +1 (469)-466-2313
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Nursing;